import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  Email,
  Lock,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorDialog from '../components/ErrorDialog';
import { useRedirectAfterLogin } from '../hooks/useRedirectAfterLogin';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorDialogKey, setErrorDialogKey] = useState(0);

  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { redirectAfterLogin } = useRedirectAfterLogin();

  // Limpiar errores al montar el componente
  useEffect(() => {
    setError('');
    setErrorCode('');
    setShowError(false);
  }, []);

  // Efecto para detectar cambios en el usuario (para debug)
  useEffect(() => {
    console.log('Login component - User state changed:', user);
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setErrorCode('');
    setShowError(false);
  };

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');
    setShowError(false);

    try {
      await login(formData.email, formData.password);
      
      // Redirigir según el progreso del usuario
      await redirectAfterLogin();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al iniciar sesión';
      const errorCode = err.response?.data?.code || '';
      
      console.log('🔍 Login Error Debug:', {
        errorMessage,
        errorCode,
        status: err.response?.status
      });
      
      setError(errorMessage);
      setErrorCode(errorCode);
      setShowError(true);
      setErrorDialogKey(prev => prev + 1); // Forzar re-render del dialog
      setLoading(false);
      console.error('Error en login:', err);
      return; // Salir aquí para evitar continuar
    }
    
    setLoading(false);
  }, [formData.email, formData.password, login, navigate]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            🛡️ Vigilantium
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Portal de E-Learning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inicia sesión para acceder a tu portal de ciberseguridad
          </Typography>
        </Box>


        <form 
          onSubmit={handleSubmit} 
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) {
              e.preventDefault();
              handleSubmit(e as any);
            }
          }}
          style={{ marginTop: '8px' }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Correo electrónico"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              if (!loading) {
                handleSubmit(e as any);
              }
            }}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Iniciar Sesión'
            )}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              ¿No tienes cuenta?
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            component={RouterLink}
            to="/register"
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            Registrarse
          </Button>
        </form>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            ¿Olvidaste tu contraseña?{' '}
            <Link component={RouterLink} to="/forgot-password" color="primary">
              Recupérala aquí
            </Link>
          </Typography>
        </Box>

      </Paper>

      {/* Error Dialog */}
      <ErrorDialog
        key={errorDialogKey}
        open={showError && !!error}
        onClose={() => {
          setShowError(false);
          setError('');
          setErrorCode('');
          setErrorDialogKey(prev => prev + 1);
        }}
        errorCode={errorCode}
        errorMessage={error}
      />
    </Box>
  );
};

export default Login; 